import { useState } from 'react';
import { View, StyleSheet, TextInput, Text, Pressable } from 'react-native';
import { widthPercentageToDP as wp } from 'react-native-responsive-screen';
import { MaterialCommunityIcons } from '@expo/vector-icons';

import { fonts } from '../style/fonts';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

export default function AppTextInput({
  leftIcon,
  secureTextShowHide,
  boxStyle = null,
  textStyle = null,
  fontSize,
  error,
  errorMessage,
  fullWidth = false,
  ...otherProps
}) {
  const { t } = useTranslation('common');

  const { isDesktop } = useResponsiveStyles();
  const [hidePassword, setHidePassword] = useState(true);
  const [icon, setIcon] = useState('eye-off');
  const [isFocused, setIsFocused] = useState(false);

  const changeIcon = () => {
    icon !== 'eye-off'
      ? (setIcon('eye-off'), setHidePassword(true))
      : (setIcon('eye'), setHidePassword(false));
  };

  // Use 100% width when fullWidth prop is true, otherwise use responsive width
  // Only apply calculated width when no custom boxStyle is provided
  const inputWidth = fullWidth ? '100%' : isDesktop ? wp(40) : wp(80);

  const containerStyle = [
    boxStyle ? boxStyle : styles.container,
    !boxStyle && { width: inputWidth },
    isFocused && styles.containerFocused,
    error && styles.containerError,
  ];

  const iconColor = error
    ? AppStyles.colorSet.error
    : isFocused
      ? AppStyles.colorSet.brandPrimary
      : AppStyles.colorSet.textSecondary;

  return (
    <View>
      <View style={containerStyle}>
        {leftIcon && (
          <MaterialCommunityIcons name={leftIcon} size={20} color={iconColor} style={styles.icon} />
        )}
        <TextInput
          adjustsFontSizeToFit={true}
          allowFontScaling={true}
          maxFontSizeMultiplier={2.0}
          style={[styles.input, textStyle ? textStyle : {}, fontSize ? { fontSize: fontSize } : {}]}
          placeholderTextColor={AppStyles.colorSet.textMuted}
          accessibilityLabel={otherProps.placeholder || t('a11y.textInput')}
          secureTextEntry={secureTextShowHide && hidePassword}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...otherProps}
        />
        {secureTextShowHide && (
          <Pressable
            onPress={changeIcon}
            accessibilityRole="button"
            accessibilityLabel={hidePassword ? t('a11y.showPassword') : t('a11y.hidePassword')}
            style={{ position: 'absolute', right: 10, top: 18, cursor: 'pointer' }}
          >
            {({ hovered, focused }) => (
              <MaterialCommunityIcons
                name={icon}
                size={20}
                color={hovered || focused ? AppStyles.colorSet.brandPrimary : iconColor}
              />
            )}
          </Pressable>
        )}
      </View>
      {error && errorMessage && (
        <Text
          style={styles.errorText}
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
        >
          {errorMessage}
        </Text>
      )}
    </View>
  );
}
const styles = StyleSheet.create({
  // V3: Clean white background with subtle border
  container: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: AppStyles.borderRadii.lg,
    flexDirection: 'row',
    padding: 15,
    marginVertical: 10,
    borderWidth: 1,
    borderColor: AppStyles.borderSet.default,
    ...AppStyles.styleSet.shadowSmall,
  },
  // V3: Gold accent on focus
  containerFocused: {
    borderColor: AppStyles.colorSet.brandPrimary,
    shadowColor: AppStyles.colorSet.brandPrimary,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 2,
  },
  containerError: {
    borderColor: AppStyles.colorSet.error,
    backgroundColor: AppStyles.colorSet.errorLight,
  },
  icon: {
    marginRight: 10,
  },
  input: {
    width: '100%',
    fontSize: 20,
    color: AppStyles.colorSet.textPrimary,
    fontFamily: fonts.POPPINS.medium,
  },
  errorText: {
    color: AppStyles.colorSet.error,
    fontSize: 12,
    marginTop: -6,
    marginBottom: 4,
    marginLeft: 4,
  },
});
